import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Megaphone, ArrowRight, ArrowLeft, Globe, Link2, Phone, Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Send, Rocket, ImageOff, Wand2, Save, Upload, Film, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api, { formatPaise, formatError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import PhonePreview from '@/components/PhonePreview';
import TopUpDialog from '@/components/TopUpDialog';
import AudienceEstimate from '@/components/AudienceEstimate';
import LocationPicker from '@/components/LocationPicker';
import AdChat from '@/components/AdChat';
import VideoAdDialog from '@/components/VideoAdDialog';
import CouponRedeem from '@/components/CouponRedeem';
import TrustPanel from '@/components/TrustPanel';
import { AD_IN_PROGRESS_KEY, ALLOWED_CTAS, GOALS, LIMITS, RETURN_AD_KEY, costBreakdown, ctaLabel, goalLabel } from '@/lib/campaign';
import confetti from 'canvas-confetti';

// The real steps of making an ad (generation_step on the server), in order.
const GEN_STEPS = [
  ['writing', 'Writing your ad'],
  ['reviewing', 'Our editor reviews it'],
  ['poster', 'Designing the poster, checking every word and choosing who sees it'],
];
const EMPTY_BRIEF = { goal: '', promotion: '', ideal_customers: '', location: '', radius_km: 10, age_min: 18, age_max: 65, gender: 'all', language: 'English', link: '', phone_whatsapp: '', locations: null };
const LAUNCHED = ['launching', 'pending_review', 'queued', 'creating', 'in_meta_review', 'live', 'paused', 'ended', 'rejected', 'cancelled'];
const digits = (s) => (s || '').replace(/\D/g, '');
const errMsg = (err, fallback) => formatError(err.response?.data?.detail) || fallback;

function Counter({ value, max }) {
  return <span className={`text-[10px] ${value.length > max ? 'text-red-700 dark:text-red-400' : 'text-muted-foreground'}`}>{value.length}/{max}</span>;
}

// The words on the poster (limits match the server's).
const IMAGE_LIMITS = { image_headline: 30, image_badge: 24, point: 24 };
const withPoints = (v) => ({ ...v, image_points: [0, 1, 2].map(k => (v.image_points || [])[k] || '') });

function VariantEditor({ campaign, variant, onSaved }) {
  const [draft, setDraft] = useState(withPoints(variant));
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(withPoints(variant)), [variant]);
  const goal = campaign.brief?.goal || 'website';
  const points = draft.image_points.map(p => p.trim()).filter(Boolean);
  const dirty = ['headline', 'primary_text', 'description', 'cta', 'image_headline', 'image_badge'].some(k => (draft[k] || '') !== (variant[k] || ''))
    || points.join('\n') !== (variant.image_points || []).join('\n');
  const tooLong = Object.entries(LIMITS).some(([k, n]) => (draft[k] || '').length > n)
    || (draft.image_headline || '').length > IMAGE_LIMITS.image_headline || !(draft.image_headline || '').trim()
    || (draft.image_badge || '').length > IMAGE_LIMITS.image_badge || points.some(p => p.length > IMAGE_LIMITS.point);
  const setPoint = (k, value) => setDraft(d => ({ ...d, image_points: d.image_points.map((p, j) => (j === k ? value : p)) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/api/campaigns/${campaign.id}/variants/${variant.id}`, {
        headline: draft.headline, primary_text: draft.primary_text, description: draft.description, cta: draft.cta,
        image_headline: draft.image_headline || '', image_badge: draft.image_badge || '', image_points: points,
      });
      onSaved(data);
      toast.success('Changes saved');
    } catch (err) {
      toast.error(errMsg(err, 'Could not save changes'));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-3 mt-3 pt-3 border-t border-border" onClick={e => e.stopPropagation()} data-testid="variant-editor">
      <div>
        <Label className="text-xs font-semibold flex justify-between mb-1">Headline <Counter value={draft.headline} max={LIMITS.headline} /></Label>
        <Input value={draft.headline} onChange={e => setDraft(d => ({ ...d, headline: e.target.value }))} data-testid="edit-headline" />
      </div>
      <div>
        <Label className="text-xs font-semibold flex justify-between mb-1">Main text <Counter value={draft.primary_text} max={LIMITS.primary_text} /></Label>
        <Textarea rows={3} value={draft.primary_text} onChange={e => setDraft(d => ({ ...d, primary_text: e.target.value }))} data-testid="edit-primary-text" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold flex justify-between mb-1">Description <Counter value={draft.description} max={LIMITS.description} /></Label>
          <Input value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} data-testid="edit-description" />
        </div>
        <div>
          <Label className="text-xs font-semibold mb-1 block">Button</Label>
          <Select value={draft.cta} onValueChange={v => setDraft(d => ({ ...d, cta: v }))}>
            <SelectTrigger data-testid="edit-cta"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(ALLOWED_CTAS[goal] || ALLOWED_CTAS.website).map(c => <SelectItem key={c} value={c}>{ctaLabel(c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="pt-2" data-testid="edit-poster-words">
        <p className="text-xs font-semibold mb-0.5">Words on the image</p>
        <p className="text-[11px] text-muted-foreground mb-2">Changes show on the image when you tap <b>New image</b>.</p>
        <div className="space-y-2">
          <div>
            <Label className="text-[11px] flex justify-between mb-1">Hook <Counter value={draft.image_headline || ''} max={IMAGE_LIMITS.image_headline} /></Label>
            <Input value={draft.image_headline || ''} onChange={e => setDraft(d => ({ ...d, image_headline: e.target.value }))} data-testid="edit-image-headline" />
          </div>
          <div>
            <Label className="text-[11px] flex justify-between mb-1">Offer (optional) <Counter value={draft.image_badge || ''} max={IMAGE_LIMITS.image_badge} /></Label>
            <Input value={draft.image_badge || ''} placeholder="e.g. 20% off this week" onChange={e => setDraft(d => ({ ...d, image_badge: e.target.value }))} data-testid="edit-image-badge" />
          </div>
          {draft.image_points.map((p, k) => (
            <div key={k}>
              <Label className="text-[11px] flex justify-between mb-1">Benefit {k + 1} <Counter value={p} max={IMAGE_LIMITS.point} /></Label>
              <Input value={p} onChange={e => setPoint(k, e.target.value)} data-testid={`edit-image-point-${k}`} />
            </div>
          ))}
        </div>
      </div>
      {dirty && (
        <Button size="sm" variant="outline" onClick={save} disabled={saving || tooLong} data-testid="save-variant-btn">
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save changes
        </Button>
      )}
      <ExtraTexts campaign={campaign} variant={variant} onSaved={onSaved} />
    </div>
  );
}

// Extra text options in the same ad: Meta mixes them with the main words and shows each person the best
// combination; your next ads learn from the ones that win. Up to 4 more of each.
const EXTRA_FIELDS = [['headline', 'Headlines'], ['primary_text', 'Main texts'], ['description', 'Descriptions']];
const MAX_EXTRA = 4;
const extrasOf = (v) => Object.fromEntries(EXTRA_FIELDS.map(([f]) => [f, ((v.extra_texts || {})[f] || []).map(o => o.text)]));

function ExtraTexts({ campaign, variant, onSaved }) {
  const [draft, setDraft] = useState(extrasOf(variant));
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(extrasOf(variant)), [variant]);
  const before = extrasOf(variant);
  const clean = (list) => list.map(t => t.trim()).filter(Boolean);
  const dirty = EXTRA_FIELDS.some(([f]) => clean(draft[f]).join('\n') !== before[f].join('\n'));
  const tooLong = EXTRA_FIELDS.some(([f]) => draft[f].some(t => t.length > LIMITS[f]));
  const set = (f, k, value) => setDraft(d => ({ ...d, [f]: d[f].map((t, j) => (j === k ? value : t)) }));
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(EXTRA_FIELDS.map(([f]) => [f, clean(draft[f])]));
      const { data } = await api.put(`/api/campaigns/${campaign.id}/variants/${variant.id}`, { extra_texts: body });
      onSaved(data);
      toast.success('Saved');
    } catch (err) {
      toast.error(errMsg(err, "Couldn't save the texts"));
    }
    setSaving(false);
  };
  return (
    <div className="pt-2 border-t border-ink/10" data-testid="extra-texts">
      <p className="text-xs font-semibold mb-0.5">More words to test</p>
      <p className="text-[11px] text-muted-foreground mb-2">One ad, several options. Meta shows each person the mix that works best, and your next ads learn from the winners.</p>
      <div className="space-y-3">
        {EXTRA_FIELDS.map(([f, label]) => (
          <div key={f}>
            <p className="text-[11px] font-semibold mb-1">{label}</p>
            <div className="space-y-1.5">
              {draft[f].map((t, k) => (
                <div key={k} className="flex items-start gap-1.5">
                  <div className="flex-1 min-w-0">
                    {f === 'primary_text'
                      ? <Textarea rows={2} value={t} onChange={e => set(f, k, e.target.value)} aria-label={`${label} option ${k + 2}`} data-testid={`extra-${f}-${k}`} />
                      : <Input value={t} onChange={e => set(f, k, e.target.value)} aria-label={`${label} option ${k + 2}`} data-testid={`extra-${f}-${k}`} />}
                    <div className="flex justify-end"><Counter value={t} max={LIMITS[f]} /></div>
                  </div>
                  <Button size="sm" variant="ghost" className="h-9 w-9 p-0 shrink-0" aria-label={`Remove this ${label.toLowerCase().slice(0, -1)}`}
                    onClick={() => setDraft(d => ({ ...d, [f]: d[f].filter((_, j) => j !== k) }))}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {draft[f].length < MAX_EXTRA && (
                <button type="button" className="text-[11px] text-brand-strong underline" onClick={() => setDraft(d => ({ ...d, [f]: [...d[f], ''] }))}>
                  + Add one
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {dirty && (
        <Button size="sm" variant="outline" className="mt-2" onClick={save} disabled={saving || tooLong} data-testid="save-extra-texts">
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save these
        </Button>
      )}
    </div>
  );
}

// Every ad needs at least ₹1,000 in total (MIN_BUDGET on the server), so short ads need more per day.
const MIN_TOTAL = 100000;
const MAX_DAILY = 500000;
// The least per day: Meta's minimum for this ad account (from the server), and enough for ₹1,000 in all.
const minDailyFor = (days, metaMin = 10000) => Math.max(Math.ceil(metaMin / 5000) * 5000, Math.ceil(MIN_TOTAL / days / 5000) * 5000);

// Asked before launching a version that still has “Must fix” problems.
const RISK_CONFIRM = 'This version still has problems Meta may reject (see “Must fix”).\n\n'
  + 'You can launch it anyway: our team checks it before it goes to Meta, usually within a few hours. '
  + 'If we or Meta can’t run it, your money comes back to your wallet.\n\nLaunch it anyway?';

export default function CreateAdPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [phase, setPhase] = useState(campaignId ? 'loading' : 'chat');
  const [chatMessages, setChatMessages] = useState([]);
  // What the chat agent suggested: { goal, goal_reason, alternatives, location_hint }.
  const [aiPick, setAiPick] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [brief, setBrief] = useState(EMPTY_BRIEF);
  const [aiAnswer, setAiAnswer] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [dailyPaise, setDailyPaise] = useState(15000);
  const [durationDays, setDurationDays] = useState(7);
  // The total ad budget is the daily budget for every day the ad runs.
  const [metaMinDaily, setMetaMinDaily] = useState(10000); // Meta's smallest daily budget for this ad
  const minDaily = minDailyFor(durationDays, metaMinDaily);
  const budgetPaise = Math.max(dailyPaise, minDaily) * durationDays;
  const [policyResult, setPolicyResult] = useState(null);
  const [loading, setLoading] = useState('');
  const [balance, setBalance] = useState(0);
  const [testMode, setTestMode] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [benchmark, setBenchmark] = useState(null); // what similar businesses pay per result (budget step)
  const [pageChoiceOpen, setPageChoiceOpen] = useState(false);
  const [videoOf, setVideoOf] = useState(null); // variant id whose video is open
  const [editFromPlan, setEditFromPlan] = useState(false); // a wizard step opened from the ad plan's "Edit"
  const [videoOptOut, setVideoOptOut] = useState(false); // the video ad is included unless switched off
  const [videoShape, setVideoShape] = useState('portrait'); // which video to launch: 'portrait' (9:16) or 'square' (1:1)
  const [acceptRisk, setAcceptRisk] = useState(false); // launching a “Must fix” version: our team reviews it first
  const [checkedLink, setCheckedLink] = useState(''); // the website link that last opened (or was accepted)
  const pollRef = useRef(null);
  const rerunPrefilled = useRef(false);
  const wantGoal = useRef(''); // the goal they left to unlock (WhatsApp), picked once their Page is ready

  // Off to connect or make a Facebook Page: keep the ad in progress (the chat, plan and wizard, or the saved ad)
  // to come back to. goal: the goal they want unlocked (opens the guide to make a Page).
  // kept: the ad to restore instead of the one on screen (the chat's plan, not yet shown).
  const toPageGuide = (goal = '', kept = null) => {
    try {
      if (campaign?.id) sessionStorage.setItem(RETURN_AD_KEY, `/create-ad/${campaign.id}`);
      else {
        sessionStorage.setItem(AD_IN_PROGRESS_KEY, JSON.stringify({ phase, step, chatMessages, aiPick, brief, editFromPlan, ...kept, goal }));
        sessionStorage.setItem(RETURN_AD_KEY, '/create-ad');
      }
    } catch { /* storage blocked: they start the ad again */ }
    navigate(goal ? '/business-profile?setup=page' : '/business-profile');
  };
  useEffect(() => {
    if (campaignId) return;
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(AD_IN_PROGRESS_KEY) || 'null');
      sessionStorage.removeItem(AD_IN_PROGRESS_KEY);
    } catch { /* nothing kept */ }
    if (!saved) return;
    setChatMessages(saved.chatMessages || []);
    setAiPick(saved.aiPick || null);
    setBrief({ ...EMPTY_BRIEF, ...saved.brief });
    setStep(saved.step || 1);
    setEditFromPlan(!!saved.editFromPlan);
    setPhase(['chat', 'plan', 'wizard'].includes(saved.phase) ? saved.phase : 'chat');
    wantGoal.current = saved.goal || '';
  }, [campaignId]);
  useEffect(() => {
    if (wantGoal.current && user?.ad_page && !user.ad_page.fallback) {
      setBrief(b => ({ ...b, goal: wantGoal.current }));
      setAiPick(p => p && { ...p, goal: wantGoal.current, goal_reason: 'WhatsApp chats, now that your Facebook Page is ready.' });
      wantGoal.current = '';
    }
  }, [user]);

  const showCampaign = useCallback((data) => {
    setCampaign(data);
    if (data.brief) setBrief({ ...EMPTY_BRIEF, ...data.brief });
    if (LAUNCHED.includes(data.status)) { navigate(`/campaigns/${data.id}`, { replace: true }); return; }
    if (data.status === 'generating') { setPhase('generating'); return; }
    if (data.status === 'ai_question') { setPhase('question'); return; }
    if (data.status === 'review' && data.variants?.length) {
      const idx = data.variants.findIndex(v => v.id === data.selected_variant_id);
      if (idx >= 0) setSelectedVariant(idx);
      // "Run again" copies a finished ad that already passed its check, so it opens at the budget.
      if (data.rerun_of && !rerunPrefilled.current) {
        rerunPrefilled.current = true;
        if (data.duration_days) setDurationDays(data.duration_days);
        if (data.budget_paise && data.duration_days) setDailyPaise(Math.round(data.budget_paise / data.duration_days / 5000) * 5000);
        setPhase('budget');
        return;
      }
      setPhase(p => (['policy', 'budget'].includes(p) ? p : 'review'));
      return;
    }
    if (data.generation_error) toast.error(data.generation_error);
    setPhase('wizard'); setStep(5);
  }, [navigate]);

  const load = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/api/campaigns/${id}`);
      showCampaign(data);
      return data;
    } catch {
      navigate('/create-ad');
    }
  }, [navigate, showCampaign]);

  useEffect(() => { if (campaignId) load(campaignId); }, [campaignId, load]);
  useEffect(() => {
    if (!campaign?.id) return;
    api.get(`/api/campaigns/${campaign.id}/budget-minimum`).then(({ data }) => setMetaMinDaily(data.min_daily_paise || 10000)).catch(() => {});
  }, [campaign?.id]);
  // A new ad needs the free ad or a plan with ads left; otherwise straight to the pricing page.
  useEffect(() => {
    if (campaignId) return;
    api.get('/api/wallet/plan').then(({ data }) => { if (!data.can_make_ad) navigate('/pricing', { replace: true }); }).catch(() => {});
  }, [campaignId, navigate]);

  const refreshBalance = () => api.get('/api/wallet/balance').then(({ data }) => { setBalance(data.balance_paise); setTestMode(!!data.test_mode); }).catch(() => {});
  useEffect(() => { refreshBalance(); }, []);
  useEffect(() => {
    if (phase !== 'budget' || !campaign?.id) return;
    api.get(`/api/campaigns/${campaign.id}/benchmark`).then(({ data }) => setBenchmark(data.benchmark)).catch(() => {});
  }, [phase, campaign?.id]);

  // Videos and new images are made in the background; poll while any is being made.
  const makingVideo = ['review', 'policy', 'budget'].includes(phase)
    && campaign?.variants?.some(v => v.video_status === 'making' || v.image_status === 'making');
  // Say when a new image is in (or failed), once.
  const imageStatus = useRef({});
  useEffect(() => {
    (campaign?.variants || []).forEach(v => {
      if (imageStatus.current[v.id] === 'making' && v.image_status === 'ready') toast.success('Your new image is ready!');
      if (imageStatus.current[v.id] === 'making' && v.image_status === 'failed') toast.error("We couldn't make a new image. Your old one is kept.");
      imageStatus.current[v.id] = v.image_status;
    });
  }, [campaign]);
  useEffect(() => {
    if (!makingVideo || !campaign?.id) return undefined;
    const timer = setInterval(() => load(campaign.id), 4000);
    return () => clearInterval(timer);
  }, [makingVideo, campaign?.id, load]);

  // Generation runs in the background on the server; poll until it's done.
  useEffect(() => {
    if (phase !== 'generating' || !campaign?.id) return undefined;
    pollRef.current = setInterval(() => load(campaign.id), 3000);
    return () => clearInterval(pollRef.current);
  }, [phase, campaign?.id, load]);

  const stepValidFor = (n) => {
    if (n === 1) return !!brief.goal;
    if (n === 2) return brief.promotion.trim().length >= 3;
    if (n === 3) return !!brief.locations && brief.age_min >= 18 && brief.age_max <= 65 && brief.age_min <= brief.age_max;
    if (n === 4) {
      if (brief.goal === 'website') return /^https?:\/\/.+\..+/.test(brief.link);
      if (brief.goal === 'calls' || brief.goal === 'whatsapp') return digits(brief.phone_whatsapp).length >= 10;
      return true;
    }
    return true;
  };
  const stepValid = () => stepValidFor(step);

  const briefFrom = (s, b) => {
    const { goal, promotion, ideal_customers, age_min, age_max, gender, language, link, phone_whatsapp } = s;
    // The agent also picks the best place to show the ad; the customer can change it on the next steps.
    const loc = s.locations;
    return {
      ...b, goal, promotion, ideal_customers, age_min, age_max, gender, language, link, phone_whatsapp,
      ...(loc ? { locations: loc, location: loc.summary, radius_km: loc.radius_km } : {}),
      // What the chat read on their website: used to write the ad and choose who sees it.
      website_facts: s.website_facts || null,
    };
  };
  const pickFrom = (s) => ({ goal: s.goal, goal_reason: s.goal_reason, alternatives: s.alternatives || [], location_hint: s.location_hint });
  const applySuggestion = (s) => {
    setBrief(b => briefFrom(s, b));
    setAiPick(pickFrom(s));
    setStep(1);
    setPhase('plan');
  };
  // From the chat, off to connect or make their Page: back to this plan, set to WhatsApp once it's ready.
  const pageFirst = (s, messages) => toPageGuide('whatsapp', {
    phase: 'plan', step: 1, chatMessages: messages, aiPick: pickFrom(s), brief: briefFrom(s, brief), editFromPlan: false,
  });

  // A website ad's link is checked to open before the ad is made; the customer can still use it anyway.
  const linkOk = async () => {
    if (brief.goal !== 'website' || !brief.link || brief.link === checkedLink) return true;
    try {
      const { data } = await api.post('/api/campaigns/check-link', { url: brief.link });
      if (!data.ok && !window.confirm(`We couldn’t open your link: ${data.reason}\n\nUse it anyway?`)) return false;
    } catch { /* the check itself failed: don't stop the customer */ }
    setCheckedLink(brief.link);
    return true;
  };
  const leaveStep = async (next) => { if (step !== 4 || await linkOk()) next(); };

  const startGeneration = async (id) => {
    await api.post(`/api/campaigns/${id}/generate`);
    setAiMsgIdx(0);
    setPhase('generating');
  };

  const createAndGenerate = async () => {
    if (!(await linkOk())) return;
    setLoading('generate');
    try {
      let current = campaign;
      if (!current) {
        const { data } = await api.post('/api/campaigns', { brief });
        current = data;
        navigate(`/create-ad/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/api/campaigns/${current.id}/brief`, { brief });
        current = data;
      }
      setCampaign(current);
      await startGeneration(current.id);
    } catch (err) {
      // 402: no free ad or plan ads left, so off to the pricing page.
      if (err.response?.status === 402) { toast.error(errMsg(err, 'Pick a plan to make more ads.')); navigate('/pricing'); }
      else toast.error(errMsg(err, 'Failed to generate ads. Please try again.'));
    }
    setLoading('');
  };

  const submitAnswer = async () => {
    if (!aiAnswer.trim()) return;
    setLoading('answer');
    try {
      await api.post(`/api/campaigns/${campaign.id}/answer`, { answer: aiAnswer });
      setAiAnswer('');
      await startGeneration(campaign.id);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to process answer'));
    }
    setLoading('');
  };

  const regenerateVariant = async (idx) => {
    setLoading(`regen-${idx}`);
    try {
      const { data } = await api.post(`/api/campaigns/${campaign.id}/regenerate/${idx}`);
      setCampaign(data);
      toast.success('New version made!');
    } catch (err) {
      toast.error(errMsg(err, 'Regeneration failed'));
    }
    setLoading('');
  };

  const makeVideo = async (variant) => {
    setLoading(`video-${variant.id}`);
    try {
      const { data } = await api.post(`/api/campaigns/${campaign.id}/variants/${variant.id}/video`);
      setCampaign(data);
      toast.success('Making your video. It takes a minute or two.');
    } catch (err) {
      toast.error(errMsg(err, "Couldn't start the video"));
    }
    setLoading('');
  };

  // Versions are policy-checked automatically; continue to launch when this one passed.
  const continueToLaunch = (index = selectedVariant) => {
    const check = campaign?.variants?.[index]?.policy_check;
    if (check?.status === 'fail') {
      if (!window.confirm(RISK_CONFIRM)) return;
      setAcceptRisk(true); setSelectedVariant(index); setPhase('budget'); return;
    }
    setAcceptRisk(false);
    if (check) { setSelectedVariant(index); setPhase('budget'); return; }
    runPolicyCheck(index); // the automatic check couldn't run: do it now
  };

  const MAX_UPLOAD_MB = 8;
  const uploadPhoto = async (variant, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Choose a photo (JPG or PNG).'); return; }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) { toast.error(`That photo is too big. Use one under ${MAX_UPLOAD_MB} MB.`); return; }
    setLoading(`upload-${variant.id}`);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('read failed'));
        reader.readAsDataURL(file);
      });
      const { data } = await api.post(`/api/campaigns/${campaign.id}/variants/${variant.id}/upload-image`, { image_base64: dataUrl });
      setCampaign(data);
      setPolicyResult(null);
      toast.success('Your photo is in!');
    } catch (err) {
      toast.error(errMsg(err, "Couldn't use that photo"));
    }
    setLoading('');
  };

  // The new image is made in the background (it can take a few minutes); the page checks back until it's in.
  const regenerateImage = async (variant) => {
    setLoading(`img-${variant.id}`);
    try {
      const { data } = await api.post(`/api/campaigns/${campaign.id}/variants/${variant.id}/regenerate-image`);
      setCampaign(data);
      toast.success('Making your new image. It takes a minute or two.');
    } catch (err) {
      toast.error(errMsg(err, "Couldn't make a new image"));
    }
    setLoading('');
  };

  // Only needed when the automatic check couldn't run: check now, then go straight to launch unless it failed.
  const runPolicyCheck = async (index = selectedVariant) => {
    const variant = campaign?.variants?.[index];
    if (!variant) return;
    setLoading('policy');
    try {
      const { data } = await api.post(`/api/campaigns/${campaign.id}/policy-check`, { variant_id: variant.id });
      setPolicyResult(data);
      setCampaign(c => ({ ...c, variants: c.variants.map(x => (x.id === variant.id ? { ...x, policy_check: data } : x)) }));
      if (data.status === 'fail') setPhase('policy');
      else { setSelectedVariant(index); setPhase('budget'); }
    } catch (err) {
      toast.error(errMsg(err, 'Policy check failed. Please try again.'));
    }
    setLoading('');
  };

  const launchCampaign = async () => {
    setPhase('launching');
    try {
      const variant = campaign?.variants?.[selectedVariant];
      const { data } = await api.post(`/api/campaigns/${campaign.id}/launch`, {
        budget_paise: budgetPaise, duration_days: durationDays, selected_variant_id: variant?.id || '',
        include_video: !videoOptOut && variant?.video_status === 'ready', video_shape: videoShape,
        accept_policy_risk: acceptRisk && variant?.policy_check?.status === 'fail',
      });
      confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 }, colors: ['#FF7A1A', '#FFC93C', '#7CC6FE', '#B8F2C9', '#141414'] });
      setCampaign(data.campaign);
      setPhase('done');
      toast.success('Campaign launched!');
    } catch (err) {
      const status = err.response?.status;
      toast.error(errMsg(err, 'Launch failed'));
      if (status === 409) { navigate(`/campaigns/${campaign.id}`); return; }
      if (status === 402) setTopUpOpen(true);
      setPhase('budget');
      refreshBalance();
    }
  };

  if (phase === 'loading') {
    return <div className="flex justify-center py-20"><div className="flex gap-1"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div></div>;
  }

  // CHAT PHASE: the customer describes the ad; the agent suggests a brief for the wizard.
  if (phase === 'chat') {
    return (
      <div data-testid="create-ad-chat" className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-heading mb-1">Make your ad</h1>
          <p className="text-sm text-muted-foreground">Describe your ad in plain words. We'll pick the best type of ad and set it up for you to check.</p>
        </div>
        {!user?.business_profile?.business_name && (
          <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-sm text-amber-700 dark:text-amber-400">
            Tip: <button className="underline" onClick={() => navigate('/business-profile')}>fill in your business profile</button> so the AI can write ads that sound like you.
          </div>
        )}
        <AdChat messages={chatMessages} setMessages={setChatMessages} onSuggestion={applySuggestion} onPageGuide={pageFirst} />
        {aiPick && (
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setPhase('plan')}>Back to my ad plan <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        )}
      </div>
    );
  }

  // PLAN PHASE: everything the chat worked out on one card; Edit opens just that step.
  if (phase === 'plan') {
    const editStep = (n) => { setEditFromPlan(true); setStep(n); setPhase('wizard'); };
    const contactKind = brief.goal === 'website' ? 'Website link' : (brief.goal === 'calls' || brief.goal === 'whatsapp') ? 'Phone number' : '';
    const who = { all: 'Everyone', male: 'Men', female: 'Women' }[brief.gender] || 'Everyone';
    const rows = [
      { step: 1, label: 'Goal', value: goalLabel(brief.goal) || 'Choose a goal', note: aiPick?.goal_reason, ok: stepValidFor(1) },
      { step: 2, label: 'Promoting', value: brief.promotion || 'Say what you\u2019re promoting', ok: stepValidFor(2) },
      { step: 2, label: 'Ideal customers', value: brief.ideal_customers || 'Anyone interested', ok: true },
      { step: 3, label: 'Where', value: brief.locations ? brief.location : 'Choose where to show your ad', ok: !!brief.locations },
      { step: 3, label: 'Who', value: `${who}, ages ${brief.age_min}–${brief.age_max} · ${brief.language}`, ok: stepValidFor(3) || !brief.locations },
      ...(contactKind ? [{ step: 4, label: contactKind, value: (brief.goal === 'website' ? brief.link : brief.phone_whatsapp) || `Add your ${contactKind.toLowerCase()}`, ok: stepValidFor(4) }] : []),
    ];
    const ready = [1, 2, 3, 4].every(stepValidFor);
    return (
      <div className="max-w-2xl mx-auto" data-testid="ad-plan">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-heading mb-1">Your ad plan</h1>
          <p className="text-sm text-muted-foreground">Here's what we set up from your chat. Change anything, then make your ads.</p>
        </div>
        <div className="rounded-2xl border-2 border-ink bg-card shadow-neo-sm divide-y-2 divide-ink/10">
          {rows.map((r, i) => (
            <div key={i} className={`flex items-start justify-between gap-3 p-4 ${r.ok ? '' : 'bg-amber-500/5'}`} data-testid={`plan-row-${i}`}>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{r.label}</p>
                <p className={`text-sm font-medium break-words ${r.ok ? '' : 'text-amber-700 dark:text-amber-400'}`}>{r.value}</p>
                {r.note && <p className="text-xs text-muted-foreground mt-0.5">{r.note}</p>}
              </div>
              <button onClick={() => editStep(r.step)} className="text-xs text-brand-strong underline shrink-0" data-testid={`plan-edit-${i}`}>
                {r.ok ? 'Edit' : 'Add'}
              </button>
            </div>
          ))}
        </div>
        {!ready && <p className="text-xs text-amber-700 dark:text-amber-400 mt-3">Fill in the highlighted item to continue.</p>}
        <div className="flex flex-wrap justify-between items-center mt-6 gap-3">
          <Button variant="outline" onClick={() => setPhase('chat')}><ArrowLeft className="w-4 h-4 mr-1" /> Back to chat</Button>
          <Button className="gradient-bg h-11" onClick={createAndGenerate}
            disabled={!ready || loading === 'generate'} data-testid="plan-generate-btn">
            {loading === 'generate' ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />} Generate my ads
          </Button>
        </div>
      </div>
    );
  }

  // WIZARD PHASE
  if (phase === 'wizard') {
    const contactNeeded = brief.goal === 'calls' || brief.goal === 'whatsapp';
    return (
      <div data-testid="create-ad-wizard" className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-heading mb-1">Make your ad</h1>
          <p className="text-sm text-muted-foreground">Tell us about your business and we'll create the perfect ad.</p>
        </div>
        {!user?.business_profile?.business_name && (
          <div className="mb-6 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-sm text-amber-700 dark:text-amber-400">
            Tip: <button className="underline" onClick={() => navigate('/business-profile')}>fill in your business profile</button> so the AI can write ads that sound like you.
          </div>
        )}
        <div className="flex items-center gap-1.5 mb-8">
          {[1, 2, 3, 4, 5].map(s => (
            <div key={s} className={`step-dot ${s === step ? 'active' : s < step ? 'completed bg-money' : 'bg-muted'}`} />
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
            {aiPick && (step === 2 || step === 4) && (
              <p className="mb-4 text-xs text-muted-foreground flex items-center gap-1"><Sparkles className="w-3 h-3 text-brand-strong" /> Filled in from your chat. Change anything you like.</p>
            )}
            {aiPick && step === 3 && (
              <p className="mb-4 text-xs text-muted-foreground flex items-center gap-1"><Sparkles className="w-3 h-3 text-brand-strong" /> {brief.locations ? "We picked the best area for your ad, plus age, gender and language from your chat. Change anything you like." : "Age, gender and language are from your chat. Choose where to show the ad."}</p>
            )}
            {step === 1 && (
              <div className="space-y-5">
                {aiPick && (
                  <div className="p-4 rounded-xl border-2 border-ink bg-butter shadow-neo-sm" data-testid="ai-goal-pick">
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3 text-brand-strong" /> Our pick for you</p>
                    <p className="text-sm font-semibold">{goalLabel(aiPick.goal)}</p>
                    {aiPick.goal_reason && <p className="text-xs text-muted-foreground mt-1">{aiPick.goal_reason}</p>}
                    {brief.goal !== aiPick.goal && (
                      <button className="text-xs text-brand-strong underline mt-2" onClick={() => setBrief(b => ({ ...b, goal: aiPick.goal }))}>Go back to our pick</button>
                    )}
                  </div>
                )}
                <Label className="text-sm font-semibold mb-2 block">{aiPick ? 'Happy with it? Or choose another:' : 'What do you want from this ad?'}</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {/* WhatsApp ads need the customer's own Page (Meta only allows the Page's linked number). */}
                  {user?.ad_page?.fallback && (
                    <button onClick={() => toPageGuide('whatsapp')} data-testid="goal-whatsapp-setup"
                      className="p-3 rounded-xl text-sm text-left border-2 border-dashed border-ink/25 bg-card hover:border-ink sm:order-last">
                      <span className="font-semibold text-muted-foreground">Get WhatsApp messages</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">Needs your own Facebook Page. <span className="text-brand-strong underline">Set it up (about 5 minutes)</span></span>
                    </button>
                  )}
                  {GOALS.filter(g => !(user?.ad_page?.fallback && g.key === 'whatsapp')).map(g => (
                    <button key={g.key} onClick={() => setBrief(b => ({ ...b, goal: g.key }))}
                      className={`p-3 rounded-xl text-sm text-left border-2 transition-all ${brief.goal === g.key ? 'border-ink bg-butter shadow-neo-sm font-semibold' : 'border-ink/20 bg-card hover:border-ink'}`}
                      data-testid={`goal-${g.key}`}>
                      <span className={`font-semibold ${brief.goal === g.key ? 'text-brand-strong' : ''}`}>{g.label}</span>
                      {aiPick?.goal === g.key && <Badge className="ml-2 text-[10px] tag-mint text-[#141414] border-ink">Recommended</Badge>}
                      {aiPick?.alternatives.includes(g.key) && <Badge variant="outline" className="ml-2 text-[10px]">Also works</Badge>}
                      <span className="block text-xs text-muted-foreground mt-0.5">{g.help}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <Label className="text-sm font-semibold mb-2 block">What are you promoting?</Label>
                  <Textarea value={brief.promotion} onChange={e => setBrief(b => ({ ...b, promotion: e.target.value }))}
                    placeholder="e.g., 20% off all cakes this weekend, New lunch menu, Free first consultation..." rows={3}
                    data-testid="promotion-input" />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Describe your ideal customers</Label>
                  <Textarea value={brief.ideal_customers} onChange={e => setBrief(b => ({ ...b, ideal_customers: e.target.value }))}
                    placeholder="e.g., Young professionals who love desserts, Mothers with kids aged 3-10..." rows={2}
                    data-testid="customers-input" />
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-5">
                <LocationPicker value={brief.locations} homeCity={aiPick?.location_hint || user?.business_profile?.city || ''}
                  onChange={loc => setBrief(b => ({ ...b, locations: loc, location: loc.summary, radius_km: loc.radius_km }))} />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Age Min</Label>
                    <Input type="number" value={brief.age_min} onChange={e => setBrief(b => ({ ...b, age_min: parseInt(e.target.value, 10) || 18 }))} min={18} max={65} />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">Age Max</Label>
                    <Input type="number" value={brief.age_max} onChange={e => setBrief(b => ({ ...b, age_max: parseInt(e.target.value, 10) || 65 }))} min={18} max={65} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="text-sm font-semibold mb-2 block">Gender</Label>
                    <Select value={brief.gender} onValueChange={v => setBrief(b => ({ ...b, gender: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {(brief.age_min < 18 || brief.age_max > 65 || brief.age_min > brief.age_max) && (
                  <p className="text-xs text-red-700 dark:text-red-400">Ages must be between 18 and 65, lowest first.</p>
                )}
                <div>
                  <Label className="text-sm font-semibold mb-2 block"><Globe className="w-3.5 h-3.5 inline mr-1" />Ad language</Label>
                  <Select value={brief.language} onValueChange={v => setBrief(b => ({ ...b, language: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="English">English</SelectItem>
                      <SelectItem value="Hindi">Hindi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {step === 4 && (
              <div className="space-y-5">
                {brief.goal === 'engagement' ? (
                  <p className="text-sm text-muted-foreground">Your ad will be a photo post that people can like, comment on and share. There's no link or button, so nothing else is needed here. Tip: this helps people get to know you, but if you want calls, messages or visits, pick one of those goals instead.</p>
                ) : brief.goal === 'leads' ? (
                  <p className="text-sm text-muted-foreground">People will fill in a short form (name, phone, city) right inside Facebook or Instagram. You'll see their details in your Page's Meta Business Suite. Nothing else is needed here.</p>
                ) : brief.goal === 'website' ? (
                  <div>
                    <Label className="text-sm font-semibold mb-2 block"><Link2 className="w-3.5 h-3.5 inline mr-1" />Where should people land?</Label>
                    <Input value={brief.link} onChange={e => setBrief(b => ({ ...b, link: e.target.value }))} placeholder="https://yourbusiness.com" data-testid="link-input" />
                    {brief.link && !/^https?:\/\/.+\..+/.test(brief.link) && <p className="text-xs text-red-700 dark:text-red-400 mt-1">Use a full link starting with https://</p>}
                  </div>
                ) : contactNeeded && (
                  <div>
                    <Label className="text-sm font-semibold mb-2 block"><Phone className="w-3.5 h-3.5 inline mr-1" />{brief.goal === 'calls' ? 'Number people should call' : 'Your WhatsApp Business number'}</Label>
                    <Input value={brief.phone_whatsapp} onChange={e => setBrief(b => ({ ...b, phone_whatsapp: e.target.value }))} placeholder="+91 9876543210" data-testid="phone-input" />
                    {brief.goal === 'whatsapp' && <p className="text-xs text-muted-foreground mt-1">This must be the WhatsApp number linked to your Facebook Page.</p>}
                  </div>
                )}
              </div>
            )}
            {step === 5 && (
              <div className="space-y-4">
                <h3 className="text-base font-semibold">Review Your Brief</h3>
                <div className="p-4 rounded-xl bg-butter/50 border-2 border-ink/20 space-y-2 text-sm break-words">
                  <p><strong>Goal:</strong> {goalLabel(brief.goal)}</p>
                  {brief.promotion && <p><strong>Promoting:</strong> {brief.promotion}</p>}
                  {brief.ideal_customers && <p><strong>Customers:</strong> {brief.ideal_customers}</p>}
                  {brief.location && <p><strong>Location:</strong> {brief.location}</p>}
                  <p><strong>Age:</strong> {brief.age_min}-{brief.age_max} | <strong>Gender:</strong> {brief.gender} | <strong>Language:</strong> {brief.language}</p>
                  {brief.goal === 'website' && <p><strong>Link:</strong> {brief.link}</p>}
                  {contactNeeded && <p><strong>Phone:</strong> {brief.phone_whatsapp}</p>}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        {editFromPlan ? (
          <div className="flex justify-end mt-8">
            <Button className="gradient-bg" disabled={!stepValid()} data-testid="wizard-done-btn"
              onClick={() => leaveStep(() => { setEditFromPlan(false); setPhase('plan'); })}>
              <CheckCircle2 className="w-4 h-4 mr-1" /> Done
            </Button>
          </div>
        ) : (
        <div className="flex flex-wrap justify-between gap-3 mt-8">
          <Button variant="outline" onClick={() => step > 1 ? setStep(step - 1) : aiPick ? setPhase('plan') : navigate('/dashboard')} data-testid="wizard-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> {step > 1 ? 'Back' : aiPick ? 'Back to ad plan' : 'Cancel'}
          </Button>
          {step < 5 ? (
            <Button className="gradient-bg" onClick={() => leaveStep(() => setStep(step + 1))} disabled={!stepValid()} data-testid="wizard-next-btn">
              Next <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button className="gradient-bg" onClick={createAndGenerate} disabled={loading === 'generate'} data-testid="generate-ads-btn">
              {loading === 'generate' ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />} Generate my ads
            </Button>
          )}
        </div>
        )}
      </div>
    );
  }

  // GENERATING PHASE
  if (phase === 'generating') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="generating-phase">
        <div className="w-20 h-20 rounded-3xl gradient-bg flex items-center justify-center mb-6 animate-pulse">
          <Megaphone className="w-9 h-9 text-[#141414]" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-extrabold mb-5 font-heading">Making your ad</h2>
        <ol className="w-full max-w-sm space-y-3 text-left mb-6" data-testid="generation-steps">
          {GEN_STEPS.map(([key, label], k) => {
            const now = Math.max(0, GEN_STEPS.findIndex(([s]) => s === campaign?.generation_step?.key));
            const state = k < now ? 'done' : k === now ? 'now' : 'next';
            return (
              <li key={key} className="flex items-start gap-3" data-testid={`generation-step-${key}`}>
                {state === 'done' && <CheckCircle2 className="w-5 h-5 shrink-0 text-money" aria-hidden="true" />}
                {state === 'now' && <Loader2 className="w-5 h-5 shrink-0 animate-spin text-brand-strong" aria-hidden="true" />}
                {state === 'next' && <span className="w-5 h-5 shrink-0 rounded-full border-2 border-ink/20" aria-hidden="true" />}
                <span>
                  <span className={`block text-sm ${state === 'next' ? 'text-muted-foreground' : 'font-semibold'}`}>{label}</span>
                  {state === 'now' && campaign?.generation_step?.detail && (
                    <span className="block text-xs text-muted-foreground">{campaign.generation_step.detail}</span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
        <p className="text-xs text-muted-foreground">This usually takes 3–5 minutes. You can leave this page and come back.</p>
      </div>
    );
  }

  // AI QUESTION PHASE
  if (phase === 'question') {
    const questions = campaign?.ai_questions || [];
    return (
      <div className="max-w-lg mx-auto text-center" data-testid="ai-question-phase">
        <div className="w-14 h-14 rounded-2xl tag-butter border-2 border-ink flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-7 h-7 text-brand-strong" />
        </div>
        <h2 className="text-xl font-extrabold mb-2 font-heading">One quick question</h2>
        <p className="text-sm text-muted-foreground mb-6">{questions[questions.length - 1]?.question}</p>
        <div className="flex gap-2">
          <Input value={aiAnswer} onChange={e => setAiAnswer(e.target.value)} placeholder="Your answer..." onKeyDown={e => e.key === 'Enter' && submitAnswer()} data-testid="ai-answer-input" />
          <Button className="gradient-bg" onClick={submitAnswer} disabled={loading === 'answer'} data-testid="submit-answer-btn">
            {loading === 'answer' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    );
  }

  // REVIEW PHASE
  if (phase === 'review') {
    const variants = campaign?.variants || [];
    return (
      <div data-testid="review-phase">
        <div className="mb-6">
          <h1 className="text-2xl font-extrabold tracking-tight font-heading mb-1">{variants.length > 1 ? 'Pick your ad' : 'Your ad is ready'}</h1>
          <p className="text-sm text-muted-foreground">
            {variants.length > 1 ? `${variants.length} versions of your ad. Pick your favourite and edit anything you like.`
              : 'Written, reviewed and improved before the picture was made. Edit anything you like, or tap New version for another.'}
          </p>
        </div>
        {campaign?.error_message && (
          <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-sm text-amber-700 dark:text-amber-400" data-testid="launch-error-notice">
            We couldn't send your ad to Meta: {campaign.error_message} Your money has been returned to your wallet. Fix it and launch again.
          </div>
        )}
        <p className="text-xs text-muted-foreground mb-4 flex items-start gap-1.5" data-testid="real-photo-tip">
          <Upload className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
          <span><b className="text-foreground">Tip:</b> a real photo of your product, shop or work usually gets more clicks than any AI image. Tap <b className="text-foreground">Upload photo</b> on a version to use yours.</span>
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4 min-w-0">
            {variants.map((v, i) => (
              <motion.div key={v.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }}
                className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${selectedVariant === i ? 'border-ink bg-card shadow-neo' : 'border-ink/20 bg-card hover:border-ink'}`}
                onClick={() => setSelectedVariant(i)} data-testid={`ad-variant-preview-card-${i}`}>
                <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
                  <Badge className={selectedVariant === i ? 'bg-brand text-[#141414] border-ink' : 'bg-muted text-muted-foreground'}>
                    Variant {String.fromCharCode(65 + i)}
                  </Badge>
                  {v.policy_check?.status === 'pass' && <span className="text-[11px] text-money flex items-center gap-1" data-testid={`policy-badge-${i}`}><CheckCircle2 className="w-3 h-3" /> Policy OK</span>}
                  {v.policy_check?.status === 'warning' && <span className="text-[11px] text-amber-700 dark:text-amber-400 flex items-center gap-1" data-testid={`policy-badge-${i}`}><AlertTriangle className="w-3 h-3" /> Check warnings</span>}
                  {v.policy_check?.status === 'fail' && <span className="text-[11px] text-red-700 dark:text-red-400 flex items-center gap-1" data-testid={`policy-badge-${i}`}><XCircle className="w-3 h-3" /> Must fix</span>}
                  {/* Wraps under the label on narrow screens; short labels on phones. */}
                  <div className="flex flex-wrap justify-end gap-1">
                    <label className={`inline-flex items-center h-7 px-2 rounded-md text-xs whitespace-nowrap hover:bg-accent ${loading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
                      onClick={(e) => e.stopPropagation()} data-testid={`upload-photo-${i}`}>
                      {loading === `upload-${v.id}` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />} Upload<span className="hidden sm:inline">&nbsp;photo</span>
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                        onChange={(e) => { uploadPhoto(v, e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs whitespace-nowrap" onClick={(e) => { e.stopPropagation(); regenerateImage(v); }} disabled={!!loading || v.image_status === 'making'}>
                      {loading === `img-${v.id}` || v.image_status === 'making' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}<span className="sm:hidden">Image</span><span className="hidden sm:inline">New image</span>
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs whitespace-nowrap" onClick={(e) => { e.stopPropagation(); regenerateVariant(i); }} disabled={!!loading}>
                      <RefreshCw className={`w-3 h-3 mr-1 ${loading === `regen-${i}` ? 'animate-spin' : ''}`} /><span className="sm:hidden">Version</span><span className="hidden sm:inline">New version</span>
                    </Button>
                  </div>
                </div>
                {v.video_status === 'ready' && (
                  <p className="text-xs text-brand-strong mb-2 flex items-center gap-1"><Film className="w-3 h-3" /> Video ready</p>
                )}
                {v.video_status === 'making' && (
                  <p className="text-xs text-brand-strong mb-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Making video…</p>
                )}
                {v.image_status === 'making' && (
                  <p className="text-xs text-brand-strong mb-2 flex items-center gap-1" data-testid={`image-making-${i}`}><Loader2 className="w-3 h-3 animate-spin" /> Making a new image… (a minute or two)</p>
                )}
                {v.image_status === 'failed' && !v.image_error && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1"><ImageOff className="w-3 h-3" /> The new image couldn't be made, so your old one is kept. Tap "New image" to try again.</p>
                )}
                {v.poster_outdated && v.image_status !== 'making' && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1" data-testid={`poster-outdated-${i}`}><AlertTriangle className="w-3 h-3" /> The image still shows the old words. Tap "New image" to update it.</p>
                )}
                {v.image_error && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1"><ImageOff className="w-3 h-3" /> The image couldn't be made. Tap "New image" to try again, or upload your own photo.</p>
                )}
                {selectedVariant === i && ['warning', 'fail'].includes(v.policy_check?.status) && v.policy_check.issues?.length > 0 && (
                  <div className="space-y-1.5 mb-2" data-testid="policy-issues">
                    {v.policy_check.issues.map((issue, k) => (
                      <div key={k} className={`p-2 rounded-lg text-xs ${issue.severity === 'fail' ? 'bg-red-500/10 text-red-800 dark:text-red-300' : 'bg-amber-500/10 text-amber-800 dark:text-amber-300'}`}>
                        <p className="font-medium">{issue.message}</p>
                        {issue.suggestion && <p className="opacity-80 mt-0.5">Suggestion: {issue.suggestion}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {selectedVariant === i ? (
                  <VariantEditor campaign={campaign} variant={v} onSaved={(data) => { setCampaign(data); setPolicyResult(null); }} />
                ) : (
                  <>
                    <h4 className="text-sm font-semibold mb-1 break-words">{v.headline}</h4>
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{v.primary_text}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{ctaLabel(v.cta)}</Badge>
                      {(v.suggested_audience?.meta_interests
                        ? [...(v.suggested_audience.meta_demographics || []), ...v.suggested_audience.meta_interests].map(m => m.name)
                        : v.suggested_audience?.interests)?.map(int => (
                        <Badge key={int} variant="outline" className="text-[10px] bg-muted/50">{int}</Badge>
                      ))}
                    </div>
                  </>
                )}
              </motion.div>
            ))}
          </div>
          <div className="flex flex-col items-center gap-4 min-w-0">
            <PhonePreview variant={variants[selectedVariant]} pageName={user?.ad_page?.page_name || user?.business_profile?.business_name || 'Your Business'} />
            {variants[selectedVariant] && (() => {
              const sel = variants[selectedVariant];
              const name = `Variant ${String.fromCharCode(65 + selectedVariant)}`;
              return (
                <div className="w-full max-w-sm p-4 rounded-2xl neo-card" data-testid="video-panel">
                  <div className="flex items-center gap-2 mb-1">
                    <Film className="w-5 h-5 text-brand-strong" />
                    <h3 className="font-semibold font-heading">Video ad</h3>
                    <Badge className="bg-blush/60 text-brand-strong border-ink/30 text-[10px]">New</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    {sel.video_status ? `A 15–30 second video of ${name} with a voiceover, made for you automatically, for Reels, Stories and feeds.`
                      : `Turn ${name} into a 15–30 second video with a voiceover, for Reels, Stories and feeds.`}
                  </p>
                  {sel.video_status === 'making' ? (
                    <Button disabled className="w-full h-11"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Making your video… usually 1–3 minutes</Button>
                  ) : sel.video_status === 'ready' ? (
                    <div className="space-y-2">
                      <Button className="w-full h-11 gradient-bg" onClick={() => setVideoOf(sel.id)} data-testid="watch-video-btn">
                        <Film className="w-4 h-4 mr-2" /> Watch, download or launch
                      </Button>
                      <Button variant="outline" className="w-full" onClick={() => makeVideo(sel)} disabled={!!loading} data-testid="remake-video-btn">
                        {loading === `video-${sel.id}` ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Make a new video
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Button className="w-full h-11 gradient-bg" onClick={() => makeVideo(sel)}
                        disabled={!!loading || sel.image_error} data-testid="make-video-btn">
                        {loading === `video-${sel.id}` ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Film className="w-4 h-4 mr-2" />}
                        {sel.video_status === 'failed' ? 'Try again' : 'Make a video ad'}
                      </Button>
                      {sel.video_status === 'failed' && <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">{sel.video_error}</p>}
                      {sel.image_error && <p className="text-xs text-muted-foreground mt-2">Make or upload an image for this version first.</p>}
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
        <VideoAdDialog variant={variants.find(v => v.id === videoOf)} open={!!videoOf} onOpenChange={(o) => !o && setVideoOf(null)}
          onLaunch={(shape) => {
            // Preview or download first, then launch: this version, with its video as its own ad set.
            const index = variants.findIndex(v => v.id === videoOf);
            setSelectedVariant(index); setVideoShape(shape); setVideoOptOut(false); setVideoOf(null); continueToLaunch(index);
          }} />
        <div className="flex flex-wrap justify-between gap-3 mt-8">
          <Button variant="outline" onClick={() => { if (aiPick) setPhase('plan'); else { setPhase('wizard'); setStep(5); } }}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Edit plan
          </Button>
          <Button className="gradient-bg" onClick={() => continueToLaunch()}
            disabled={!!loading || variants[selectedVariant]?.image_error || variants[selectedVariant]?.image_status === 'making'}
            data-testid="continue-to-launch-btn">
            {loading === 'policy' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Rocket className="w-4 h-4 mr-1" />}
            Continue to launch
          </Button>
        </div>
      </div>
    );
  }

  // POLICY CHECK PHASE
  if (phase === 'policy') {
    return (
      <div className="max-w-lg mx-auto" data-testid="policy-phase">
        <div className="text-center mb-6">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${
            policyResult?.status === 'pass' ? 'bg-money-soft' : policyResult?.status === 'warning' ? 'bg-amber-500/10' : 'bg-red-500/10'
          }`}>
            {policyResult?.status === 'pass' ? <CheckCircle2 className="w-7 h-7 text-money" /> :
             policyResult?.status === 'warning' ? <AlertTriangle className="w-7 h-7 text-amber-700 dark:text-amber-400" /> :
             <XCircle className="w-7 h-7 text-red-700 dark:text-red-400" />}
          </div>
          <h2 className="text-xl font-extrabold font-heading">
            {policyResult?.status === 'pass' ? 'All Clear!' : policyResult?.status === 'warning' ? 'Minor Issues Found' : 'Please fix these before launching'}
          </h2>
        </div>
        {policyResult?.issues?.length > 0 && (
          <div className="space-y-2 mb-6">
            {policyResult.issues.map((issue, i) => (
              <div key={i} className={`p-3 rounded-xl text-sm ${issue.severity === 'fail' ? 'bg-red-500/10 border border-red-500/20' : 'bg-amber-500/10 border border-amber-500/20'}`}>
                <p className="font-medium">{issue.message}</p>
                {issue.suggestion && <p className="text-xs text-muted-foreground mt-1">Suggestion: {issue.suggestion}</p>}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setPhase('review')} className="flex-1">
            <ArrowLeft className="w-4 h-4 mr-1" /> Edit Ads
          </Button>
          {policyResult?.status !== 'fail' ? (
            <Button className="flex-1 gradient-bg" onClick={() => { setAcceptRisk(false); setPhase('budget'); }} data-testid="proceed-budget-btn">
              Set Budget <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button variant="outline" className="flex-1" data-testid="launch-anyway-btn"
              onClick={() => { if (window.confirm(RISK_CONFIRM)) { setAcceptRisk(true); setPhase('budget'); } }}>
              Launch anyway <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // BUDGET PHASE
  if (phase === 'budget') {
    const cost = costBreakdown(budgetPaise);
    // In test mode (sandbox) launching takes no money.
    const short = testMode ? 0 : cost.total - balance;
    return (
      <div className="max-w-lg mx-auto" data-testid="budget-phase">
        <h1 className="text-2xl font-extrabold tracking-tight font-heading mb-1">Set your budget and launch</h1>
        <p className="text-sm text-muted-foreground mb-6">Choose how much to spend each day and for how many days. You only pay for what Meta spends.</p>
        <AudienceEstimate campaignId={campaign?.id} variantId={campaign?.variants?.[selectedVariant]?.id}
          dailyPaise={Math.max(dailyPaise, minDaily)} />
        {acceptRisk && campaign?.variants?.[selectedVariant]?.policy_check?.status === 'fail' && (
          <div className="mb-6 p-3 rounded-xl bg-amber-500/10 border-2 border-amber-500/40 text-sm flex items-start gap-2" data-testid="policy-risk-notice">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-700 dark:text-amber-400" />
            <span>This version has policy problems, so our team checks it before it goes to Meta (usually within a few hours). If it can&apos;t run, your money comes back.</span>
          </div>
        )}
        {campaign?.rerun_of && (
          <div className="mb-6 p-3 rounded-xl bg-butter border-2 border-ink shadow-neo-sm flex items-center justify-between gap-3 text-sm" data-testid="rerun-notice">
            <span>Running your ad again with the same text, image and video.</span>
            <button className="underline font-semibold shrink-0" onClick={() => setPhase('review')}>Edit first</button>
          </div>
        )}

        <div className="space-y-6">
          <div>
            <Label className="text-sm font-semibold mb-3 block">Budget per day</Label>
            <Slider value={[Math.max(dailyPaise, minDaily) / 100]} min={minDaily / 100} max={MAX_DAILY / 100} step={50}
              onValueChange={([v]) => setDailyPaise(v * 100)} data-testid="budget-slider" />
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 mt-2 text-sm">
              <span className="text-muted-foreground">Min {formatPaise(minDaily)}/day</span>
              <span className="font-bold text-lg gradient-text">{formatPaise(Math.max(dailyPaise, minDaily))}/day</span>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold mb-3 block">Run for: {durationDays} days</Label>
            <Slider value={[durationDays]} min={3} max={30} step={1}
              onValueChange={([v]) => setDurationDays(v)} data-testid="duration-slider" />
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>3 days</span><span>30 days</span>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-butter border-2 border-ink shadow-neo-sm flex items-center justify-between gap-3" data-testid="budget-total">
            <span className="text-sm font-medium">{formatPaise(Math.max(dailyPaise, minDaily))} × {durationDays} days</span>
            <span className="font-heading font-extrabold text-xl text-right">{formatPaise(budgetPaise)}</span>
          </div>
          {benchmark && (() => {
            // What to expect, from what similar businesses paid per result (services/benchmarks.py).
            const b = benchmark;
            const perDay = Math.max(dailyPaise, minDaily) / b.cpr_paise;
            return (
              <p className="text-xs text-muted-foreground -mt-3" data-testid="budget-benchmark">
                Similar ads ({b.scope}) pay about <b className="text-foreground">{formatPaise(b.cpr_paise)}</b> per result, so expect roughly{' '}
                <b className="text-foreground">{perDay >= 1 ? `${Math.round(perDay)} a day` : `1 every ${Math.ceil(1 / perDay)} days`}</b>{' '}
                ({Math.round(perDay * durationDays)} in {durationDays} days). Results vary, especially in the first few days.
              </p>
            );
          })()}

          <div className="neo-card p-4 space-y-2" data-testid="cost-breakdown">
            <h4 className="text-sm font-bold mb-2">What you pay</h4>
            <div className="flex justify-between gap-3 text-sm"><span>Ad spend, {durationDays} days (goes to Meta)</span><span className="font-semibold">{formatPaise(cost.spend)}</span></div>
            <div className="flex justify-between gap-3 text-sm"><span className="text-muted-foreground">Our fee (15%)</span><span>₹{(cost.fee / 100).toFixed(2)}</span></div>
            <div className="flex justify-between gap-3 text-sm"><span className="text-muted-foreground">GST (18% of the fee)</span><span>₹{(cost.gst / 100).toFixed(2)}</span></div>
            <div className="border-t-2 border-dashed border-ink mt-2 pt-2 flex justify-between gap-3 text-base font-bold">
              <span>Held from your wallet</span><span>₹{(cost.total / 100).toFixed(2)}</span>
            </div>
            <p className="text-xs text-muted-foreground pt-1">You&apos;re only charged for what Meta actually spends (plus the fee on that). The rest comes back to your wallet.</p>
            <TrustPanel variant="compact" className="pt-2 border-t border-ink/10" />
          </div>

          {testMode && (
            <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-sm text-blue-800 dark:text-blue-300" data-testid="test-mode-notice">
              Test mode: this ad runs on Meta's test account, so nothing is taken from your wallet and no real people see it.
            </div>
          )}
          {(() => {
            const sel = campaign?.variants?.[selectedVariant];
            if (!sel) return null;
            const media = (u) => (u?.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
            const ready = sel.video_status === 'ready' && sel.videos?.[videoShape];
            return (
              <div className="p-3 rounded-xl border-2 border-ink space-y-3" data-testid="launch-previews">
                <p className="text-sm font-semibold">Your ads</p>
                <div className="grid grid-cols-2 gap-3 items-start">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Image ad</p>
                    {sel.image_url ? <img src={media(sel.image_url)} alt="Your image ad" className="w-full aspect-square object-cover rounded-lg bg-muted" />
                      : <div className="w-full aspect-square rounded-lg bg-muted" />}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Video ad</p>
                    {ready ? (
                      <video key={sel.videos[videoShape]} src={media(sel.videos[videoShape])} controls playsInline
                        className={`w-full rounded-lg bg-black ${videoShape === 'portrait' ? 'aspect-[9/16]' : 'aspect-square'} ${videoOptOut ? 'opacity-40' : ''}`}
                        data-testid="launch-video-preview" />
                    ) : (
                      <div className="w-full aspect-square rounded-lg bg-muted flex flex-col items-center justify-center text-center p-2 text-xs text-muted-foreground">
                        {sel.video_status === 'making' ? <><Loader2 className="w-5 h-5 mb-1 animate-spin text-brand-strong" /> Being made… usually 1–3 minutes</>
                          : sel.video_status === 'failed' ? <>The video couldn't be made. <button className="underline mt-1" onClick={() => makeVideo(sel)}>Try again</button></>
                          : <>No video yet. <button className="underline mt-1" onClick={() => makeVideo(sel)}>Make one</button></>}
                      </div>
                    )}
                  </div>
                </div>
                {ready && (
                  <>
                    <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                      <input type="checkbox" checked={!videoOptOut} onChange={e => setVideoOptOut(!e.target.checked)} data-testid="include-video-checkbox" />
                      Also run my video ad
                    </label>
                    {!videoOptOut && (
                      <div className="flex flex-wrap gap-2 pl-6">
                        {[['portrait', '9:16 for Reels & Stories'], ['square', '1:1 for feeds']].map(([key, label]) => (
                          <button key={key} onClick={() => setVideoShape(key)}
                            className={`px-2.5 py-1 rounded-lg text-xs border-2 ${videoShape === key ? 'border-ink bg-butter font-semibold' : 'border-ink/20 text-muted-foreground'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">The video runs as its own ad next to your image ad. Your budget is shared: Meta spends more on whichever brings cheaper results.</p>
                  </>
                )}
                {sel.video_status === 'making' && (
                  <p className="text-xs text-muted-foreground">You can wait for the video, or launch now with just the image ad.</p>
                )}
              </div>
            );
          })()}
          {user?.ad_page?.fallback && (
            <div className="p-3 rounded-xl bg-butter/50 border-2 border-ink/20 text-xs text-muted-foreground" data-testid="default-page-notice">
              This ad will run from our Page, <strong>{user.ad_page.page_name || 'Rayogent'}</strong>, so that name shows above your ad.{' '}
              <button className="underline" onClick={() => toPageGuide()}>Connect your own Facebook Page</button> to show your business name instead.
            </div>
          )}
          <div className="p-3 rounded-xl bg-card border-2 border-ink flex items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">Wallet Balance</span>
            <span className="text-sm font-bold text-money">₹{(balance / 100).toFixed(2)}</span>
          </div>
          {short > 0 && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-sm text-amber-700 dark:text-amber-400 flex flex-wrap items-center justify-between gap-3">
              <span>You need ₹{(short / 100).toFixed(2)} more.</span>
              <Button size="sm" className="gradient-bg" onClick={() => setTopUpOpen(true)} data-testid="budget-topup-btn">Add money</Button>
            </div>
          )}
          {!testMode && <CouponRedeem onRedeemed={(b) => setBalance(b)} />}
        </div>

        <div className="flex gap-3 mt-8">
          <Button variant="outline" onClick={() => setPhase('review')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button className="flex-1 gradient-bg h-11"
            onClick={() => (user?.ad_page?.fallback ? setPageChoiceOpen(true) : launchCampaign())}
            disabled={short > 0} data-testid="launch-campaign-button">
            <Rocket className="w-4 h-4 mr-1.5" /> Launch Campaign
          </Button>
        </div>
        <Dialog open={pageChoiceOpen} onOpenChange={setPageChoiceOpen}>
          <DialogContent data-testid="page-choice-dialog">
            <DialogHeader>
              <DialogTitle>Which Facebook Page should run this ad?</DialogTitle>
              <DialogDescription>
                You haven't connected your own Facebook Page, so this ad will run from our Page,{' '}
                <strong>{user?.ad_page?.page_name || 'Rayogent'}</strong>. People will see that name above your ad.
                Connect your own Page to show your business name instead. Your ad is saved either way.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" data-testid="connect-own-page-btn" onClick={() => toPageGuide()}>
                Use my own Facebook Page
              </Button>
              <Button className="gradient-bg" data-testid="launch-with-default-page-btn"
                onClick={() => { setPageChoiceOpen(false); launchCampaign(); }}>
                <Rocket className="w-4 h-4 mr-1.5" /> Launch with {user?.ad_page?.page_name || 'our Page'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <TopUpDialog open={topUpOpen} onOpenChange={setTopUpOpen} suggested={Math.max(Math.ceil(short / 100) * 100, 50000)}
          onPaid={(b) => setBalance(b)} />
      </div>
    );
  }

  // LAUNCHING PHASE
  if (phase === 'launching') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div className="w-20 h-20 rounded-3xl gradient-bg flex items-center justify-center mb-6 animate-bounce">
          <Rocket className="w-9 h-9 text-[#141414]" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-extrabold font-heading">Launching your ad…</h2>
        <div className="flex gap-1 mt-4"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div>
      </div>
    );
  }

  // DONE PHASE
  if (phase === 'done') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="launch-success">
        <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', bounce: 0.5 }}>
          <div className="w-20 h-20 rounded-3xl bg-money-soft flex items-center justify-center mb-6 mx-auto">
            <CheckCircle2 className="w-10 h-10 text-money" />
          </div>
        </motion.div>
        <h2 className="text-2xl font-extrabold font-heading mb-2">Your ad is submitted! 🚀</h2>
        <p className="text-sm text-muted-foreground mb-6 max-w-sm">
          {campaign?.status === 'pending_review'
            ? "Our team gives first ads a quick check (usually within a few hours). Then it goes to Meta for review."
            : "It's on its way to Meta for review. We'll show it as LIVE once Meta approves it."}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button variant="outline" onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          <Button className="gradient-bg" onClick={() => navigate(`/campaigns/${campaign?.id || campaign?._id}`)}>
            View Campaign
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
